import React, { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../hooks/useUser'
import UsersManagement from '../users/UsersManagement'

interface CompanySettings {
  name: string
  city: string
  address: string
  nip: string
  logo_url?: string
}

interface EmailSettings {
  smtp_host: string
  smtp_port: number
  smtp_username: string
  smtp_password: string
  use_tls: boolean
  from_email: string
  from_name: string
  is_active: boolean
}

interface SystemSettings {
  company: CompanySettings
  email_notifications: boolean
  auto_backup: boolean
  backup_frequency: string
}

interface PasswordChangeFormProps {
  token: string
}

function PasswordChangeForm({ token }: PasswordChangeFormProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const { showSuccess, showError } = useNotificationContext()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      showError('Nowe hasła nie są identyczne')
      return
    }

    if (newPassword.length < 6) {
      showError('Nowe hasło musi mieć co najmniej 6 znaków')
      return
    }

    setLoading(true)
    try {
      const response = await fetch('/api/users/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      })

      if (response.ok) {
        showSuccess('Hasło zostało zmienione pomyślnie')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas zmiany hasła')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Aktualne hasło
        </label>
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Nowe hasło
        </label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          Hasło musi mieć co najmniej 6 znaków
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Potwierdź nowe hasło
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              Zmiana hasła...
            </>
          ) : (
            'Zmień hasło'
          )}
        </button>
      </div>
    </form>
  )
}

interface SettingsPageProps {
  token: string
}

function ApiKeysManagement({ token }: { token: string }) {
  const [apiKeys, setApiKeys] = useState<any[]>([])
  const [clients, setClients] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newKeyForm, setNewKeyForm] = useState({
    client_id: 0,
    name: '',
    expires_in_days: 30
  })
  const [kioskForm, setKioskForm] = useState({
    client_id: 0,
    device_name: '',
    expires_minutes: 15,
  })
  const [creatingKioskCode, setCreatingKioskCode] = useState(false)
  const [kioskPairingResult, setKioskPairingResult] = useState<any | null>(null)
  const [kioskPairingCodes, setKioskPairingCodes] = useState<any[]>([])
  const { showSuccess, showError } = useNotificationContext()

  const extractErrorMessage = (errorData: any): string => {
    if (!errorData) return 'Wystąpił błąd'
    if (typeof errorData === 'string') return errorData
    if (typeof errorData?.detail === 'string') return errorData.detail
    if (Array.isArray(errorData?.detail) && errorData.detail.length > 0) {
      const first = errorData.detail[0]
      if (typeof first === 'string') return first
      if (first?.msg) return first.msg
    }
    return 'Błąd walidacji danych. Sprawdź formularz.'
  }

  useEffect(() => {
    fetchApiKeys()
    fetchClients()
    fetchKioskPairingCodes()
  }, [])

  const fetchKioskPairingCodes = async () => {
    try {
      const response = await fetch('/api/kiosk/pairing-codes?only_active=true', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setKioskPairingCodes(data)
      }
    } catch (error) {
      console.error('Błąd pobierania kodów kiosk:', error)
    }
  }

  const fetchApiKeys = async () => {
    try {
      const response = await fetch('/api/api-keys', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setApiKeys(data)
      }
    } catch (error) {
      showError('Błąd podczas ładowania kluczy API')
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    try {
      const response = await fetch('/api/api-keys/clients', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const handleCreateKey = async () => {
    if (!newKeyForm.client_id || !newKeyForm.name.trim()) {
      showError('Wybierz klienta i wprowadź nazwę klucza')
      return
    }

    console.log('Creating API key with data:', newKeyForm)

    try {
      const response = await fetch('/api/api-keys', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newKeyForm),
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', response.headers)

      if (response.ok) {
        const data = await response.json()
        console.log('Response data:', data)
        showSuccess('Klucz API został utworzony')
        setNewKeyForm({ client_id: 0, name: '', expires_in_days: 30 })
        setShowCreateForm(false)
        fetchApiKeys()
      } else {
        const errorText = await response.text()
        console.error('Error response:', errorText)
        try {
          const error = JSON.parse(errorText)
          showError(error.detail || 'Błąd podczas tworzenia klucza API')
        } catch (parseError) {
          console.error('Error parsing error response:', parseError)
          showError(`Błąd HTTP ${response.status}: ${errorText}`)
        }
      }
    } catch (error) {
      console.error('Network error:', error)
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleToggleKey = async (keyId: number) => {
    try {
      const response = await fetch(`/api/api-keys/${keyId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (response.ok) {
        showSuccess('Status klucza został zmieniony')
        fetchApiKeys()
      } else {
        showError('Błąd podczas zmiany statusu klucza')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleDeleteKey = async (keyId: number) => {
    if (!confirm('Czy na pewno chcesz usunąć ten klucz API?')) return

    try {
      const response = await fetch(`/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (response.ok) {
        showSuccess('Klucz API został usunięty')
        fetchApiKeys()
      } else {
        showError('Błąd podczas usuwania klucza')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleCreateKioskPairingCode = async () => {
    if (!kioskForm.client_id) {
      showError('Wybierz klienta dla urządzenia kiosk')
      return
    }

    setCreatingKioskCode(true)
    try {
      const response = await fetch('/api/kiosk/pairing-codes', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(kioskForm),
      })

      if (response.ok) {
        const data = await response.json()
        setKioskPairingResult(data)
        fetchKioskPairingCodes()
        showSuccess('Kod parowania kiosk został wygenerowany')
      } else {
        const error = await response.json()
        showError(extractErrorMessage(error))
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setCreatingKioskCode(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Zarządzanie kluczami API
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Klucze API służą do autoryzacji integratorów zewnętrznych
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          + Nowy klucz API
        </button>
      </div>

      {/* Create Key Form */}
      {showCreateForm && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Utwórz nowy klucz API</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Klient</label>
              <select
                value={newKeyForm.client_id}
                onChange={(e) => setNewKeyForm(prev => ({ ...prev, client_id: parseInt(e.target.value) }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Wybierz klienta</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Nazwa klucza</label>
              <input
                type="text"
                value={newKeyForm.name}
                onChange={(e) => setNewKeyForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="np. System zgłoszeń"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Wygasa za (dni)</label>
              <input
                type="number"
                value={newKeyForm.expires_in_days}
                onChange={(e) => setNewKeyForm(prev => ({ ...prev, expires_in_days: parseInt(e.target.value) || 30 }))}
                min="1"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Anuluj
            </button>
            <button
              onClick={handleCreateKey}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Utwórz klucz
            </button>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {apiKeys.length === 0 ? (
            <li className="px-6 py-4 text-center text-gray-500">
              Brak kluczy API
            </li>
          ) : (
            apiKeys.map((key) => (
              <li key={key.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">{key.name}</p>
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        key.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {key.is_active ? 'Aktywny' : 'Nieaktywny'}
                      </span>
                      {key.is_expired && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Wygasł
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center text-sm text-gray-600">
                      <span className="font-medium">{key.client_name}</span>
                      <span className="mx-2">•</span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {key.key.substring(0, 20)}...
                      </span>
                      <button
                        onClick={() => navigator.clipboard.writeText(key.key)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        title="Kopiuj pełny klucz"
                      >
                        📋
                      </button>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Utworzony: {new Date(key.created_at).toLocaleDateString('pl-PL')}
                      {key.expires_at && (
                        <> • Wygasa: {new Date(key.expires_at).toLocaleDateString('pl-PL')}</>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleKey(key.id)}
                      className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                        key.is_active
                          ? 'text-orange-700 bg-orange-100 hover:bg-orange-200'
                          : 'text-green-700 bg-green-100 hover:bg-green-200'
                      }`}
                    >
                      {key.is_active ? 'Dezaktywuj' : 'Aktywuj'}
                    </button>
                    <button
                      onClick={() => handleDeleteKey(key.id)}
                      className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200"
                    >
                      Usuń
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Jak używać kluczy API</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Klucze API są używane przez integratorów zewnętrznych do dostępu do API zgłoszeń.</p>
              <p className="mt-1">W nagłówku żądań należy podawać: <code className="bg-blue-100 px-1 rounded">X-API-Key: [twój_klucz]</code></p>
            </div>
          </div>
        </div>
      </div>

      {/* Kiosk Pairing */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 space-y-4">
        <div>
          <h3 className="text-sm font-medium text-indigo-900">Parowanie kiosku (/services-display)</h3>
          <p className="mt-1 text-sm text-indigo-700">
            Tutaj wygenerujesz jednorazowy kod dla ekranu kiosk.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Klient</label>
            <select
              value={kioskForm.client_id}
              onChange={(e) => setKioskForm(prev => ({ ...prev, client_id: parseInt(e.target.value) || 0 }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">Wybierz klienta</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Nazwa urządzenia (opcjonalnie)</label>
            <input
              type="text"
              value={kioskForm.device_name}
              onChange={(e) => setKioskForm(prev => ({ ...prev, device_name: e.target.value }))}
              placeholder="np. TV - dyspozytornia"
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Ważność kodu (minuty)</label>
            <input
              type="number"
              min={1}
              max={525600}
              value={kioskForm.expires_minutes}
              onChange={(e) => setKioskForm(prev => ({ ...prev, expires_minutes: parseInt(e.target.value) || 15 }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleCreateKioskPairingCode}
            disabled={creatingKioskCode}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {creatingKioskCode ? 'Generowanie...' : 'Wygeneruj kod kiosk'}
          </button>
        </div>

        {kioskPairingResult && (
          <div className="bg-white border border-indigo-200 rounded-md p-4 space-y-2">
            <p className="text-sm"><span className="font-medium">Kod:</span> <code>{kioskPairingResult.pairing_code}</code></p>
            <p className="text-sm"><span className="font-medium">Wygasa:</span> {new Date(kioskPairingResult.pairing_expires_at).toLocaleString('pl-PL')}</p>
            <p className="text-sm break-all"><span className="font-medium">Link parowania:</span> {window.location.origin}/services-display?pairing_code={kioskPairingResult.pairing_code}</p>
            <button
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/services-display?pairing_code=${kioskPairingResult.pairing_code}`)}
              className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
            >
              📋 Kopiuj link
            </button>
          </div>
        )}

        <div className="bg-white border border-indigo-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-indigo-900">Aktywne kody parowania</h4>
            <button
              onClick={fetchKioskPairingCodes}
              className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
            >
              Odśwież
            </button>
          </div>

          {kioskPairingCodes.length === 0 ? (
            <p className="text-sm text-gray-500">Brak aktywnych kodów (wszystkie wykorzystane lub wygasłe).</p>
          ) : (
            <div className="space-y-2">
              {kioskPairingCodes.map((item) => (
                <div key={item.device_id} className="border border-gray-200 rounded p-2 text-sm">
                  <p><span className="font-medium">Kod:</span> <code>{item.pairing_code}</code></p>
                  <p><span className="font-medium">Klient:</span> {item.client_name}</p>
                  {item.device_name && <p><span className="font-medium">Urządzenie:</span> {item.device_name}</p>}
                  <p><span className="font-medium">Wygasa:</span> {item.pairing_expires_at ? new Date(item.pairing_expires_at).toLocaleString('pl-PL') : '-'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function SettingsPage({ token }: SettingsPageProps) {
  const [settings, setSettings] = useState<SystemSettings>({
    company: {
      name: '',
      city: '',
      address: '',
      nip: '',
      logo_url: undefined
    },
    email_notifications: true,
    auto_backup: false,
    backup_frequency: 'daily'
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    use_tls: true,
    from_email: '',
    from_name: '',
    is_active: false
  })
  const [testingEmail, setTestingEmail] = useState(false)
  const [activeTab, setActiveTab] = useState<'company' | 'email' | 'account' | 'users' | 'api-keys'>('company')
  // Ustawienia są PER REFERAT - wybór referatu, którego ustawienia edytujemy
  const [clientsList, setClientsList] = useState<Array<{ id: number; name: string }>>([])
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null)
  const { user } = useUser(token)
  const { showSuccess, showError } = useNotificationContext()

  // Dokleja ?client_id=... do adresu ustawień (per referat)
  const withClient = (url: string) =>
    selectedClientId ? `${url}${url.includes('?') ? '&' : '?'}client_id=${selectedClientId}` : url

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MASTER_ADMIN'

  useEffect(() => {
    if (!user) return
    if (isAdmin) fetchClients()
    else setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (selectedClientId != null) {
      fetchSettings()
      fetchEmailSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId])

  const fetchClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClientsList(data)
        if (data.length > 0) setSelectedClientId(data[0].id)
        else setLoading(false)
      } else {
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
    }
  }

  const fetchSettings = async () => {
    try {
      const response = await fetch(withClient('/api/settings/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSettings(data)
        setLogoPreview(data.company.logo_url || null)
      } else {
        showError('Błąd podczas ładowania ustawień')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // First upload logo if selected
      if (logoFile) {
        const formData = new FormData()
        formData.append('file', logoFile)

        const logoResponse = await fetch(withClient('/api/settings/logo'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        })

        if (logoResponse.ok) {
          const logoData = await logoResponse.json()
          setSettings(prev => ({
            ...prev,
            company: {
              ...prev.company,
              logo_url: logoData.logo_url
            }
          }))
        }
      }

      // Save settings
      const response = await fetch(withClient('/api/settings/'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      })

      if (response.ok) {
        showSuccess('Ustawienia zostały zapisane')
        setLogoFile(null)
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas zapisywania ustawień')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setSaving(false)
    }
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        setLogoFile(file)
        const reader = new FileReader()
        reader.onload = (e) => {
          setLogoPreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        showError('Proszę wybrać plik obrazu')
      }
    }
  }

  const handleDeleteLogo = async () => {
    try {
      const response = await fetch(withClient('/api/settings/logo'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        setSettings(prev => ({
          ...prev,
          company: {
            ...prev.company,
            logo_url: undefined
          }
        }))
        setLogoPreview(null)
        setLogoFile(null)
        showSuccess('Logo zostało usunięte')
      } else {
        showError('Błąd podczas usuwania logo')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const fetchEmailSettings = async () => {
    try {
      const response = await fetch(withClient('/api/settings/email'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setEmailSettings(data)
      }
    } catch (error) {
      console.error('Error fetching email settings:', error)
    }
  }

  const handleSaveEmailSettings = async () => {
    setSaving(true)
    try {
      const response = await fetch(withClient('/api/settings/email'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailSettings),
      })

      if (response.ok) {
        showSuccess('Ustawienia email zostały zapisane')
        await fetchEmailSettings()
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas zapisywania ustawień email')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setSaving(false)
    }
  }

  const handleTestEmail = async () => {
    setTestingEmail(true)
    try {
      const response = await fetch(withClient('/api/settings/email/test'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailSettings),
      })

      if (response.ok) {
        showSuccess('Email testowy został wysłany pomyślnie')
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas wysyłania emaila testowego')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setTestingEmail(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {(user?.role === 'ADMIN' || user?.role === 'MASTER_ADMIN') ? (
        /* Admin Settings - Full Access */
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                👥 Zarządzanie użytkownikami
              </button>
              <button
                onClick={() => setActiveTab('api-keys')}
                className={`${
                  activeTab === 'api-keys'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                � Klucze API
              </button>
              <button
                onClick={() => setActiveTab('email')}
                className={`${
                  activeTab === 'email'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                �📧 Email
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`${
                  activeTab === 'account'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                � Konto
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                �👥 Zarządzanie użytkownikami
              </button>
            </nav>
          </div>

          <div className="p-6">
            {(activeTab === 'company' || activeTab === 'email') && clientsList.length > 0 && (
              <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                <span className="text-sm font-medium text-gray-700">Referat:</span>
                {clientsList.length > 1 ? (
                  <select
                    value={selectedClientId ?? ''}
                    onChange={(e) => setSelectedClientId(parseInt(e.target.value) || null)}
                    className="min-h-[40px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  >
                    {clientsList.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-semibold text-gray-900">{clientsList[0].name}</span>
                )}
                <span className="text-xs text-gray-500">Ustawienia (dane firmy, logo, SMTP) są osobne dla każdego referatu.</span>
              </div>
            )}
            {activeTab === 'company' && (
              <div className="space-y-6">
                {/* Company Settings */}
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Dane firmy
                  </h3>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nazwa firmy
                      </label>
                      <input
                        type="text"
                        value={settings.company.name}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          company: { ...prev.company, name: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Miasto
                      </label>
                      <input
                        type="text"
                        value={settings.company.city}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          company: { ...prev.company, city: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Adres
                      </label>
                      <input
                        type="text"
                        value={settings.company.address}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          company: { ...prev.company, address: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        NIP
                      </label>
                      <input
                        type="text"
                        value={settings.company.nip}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          company: { ...prev.company, nip: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Logo Section */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo firmy
                    </label>

                    <div className="flex items-center space-x-4">
                      {logoPreview && (
                        <div className="flex-shrink-0">
                          <img
                            src={logoPreview}
                            alt="Logo firmy"
                            className="h-16 w-16 object-contain border border-gray-300 rounded"
                          />
                        </div>
                      )}

                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          PNG, JPG, JPEG do 5MB
                        </p>
                      </div>

                      {logoPreview && (
                        <button
                          type="button"
                          onClick={handleDeleteLogo}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Usuń logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* System Settings */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Ustawienia systemowe
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        id="email-notifications"
                        type="checkbox"
                        checked={settings.email_notifications}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          email_notifications: e.target.checked
                        }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="email-notifications" className="ml-2 block text-sm text-gray-900">
                        Powiadomienia email
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="auto-backup"
                        type="checkbox"
                        checked={settings.auto_backup}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          auto_backup: e.target.checked
                        }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="auto-backup" className="ml-2 block text-sm text-gray-900">
                        Automatyczne kopie zapasowe
                      </label>
                    </div>

                    {settings.auto_backup && (
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700">
                          Częstotliwość kopii zapasowych
                        </label>
                        <select
                          value={settings.backup_frequency}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            backup_frequency: e.target.value
                          }))}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                          <option value="daily">Codziennie</option>
                          <option value="weekly">Tygodniowo</option>
                          <option value="monthly">Miesięcznie</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end border-t border-gray-200 pt-6">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Zapisywanie...
                      </>
                    ) : (
                      'Zapisz ustawienia'
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="space-y-6">
                {/* Email Settings */}
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Konfiguracja serwera SMTP
                  </h3>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Serwer SMTP
                      </label>
                      <input
                        type="text"
                        value={emailSettings.smtp_host}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          smtp_host: e.target.value
                        }))}
                        placeholder="smtp.gmail.com"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Port SMTP
                      </label>
                      <input
                        type="number"
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          smtp_port: parseInt(e.target.value) || 587
                        }))}
                        placeholder="587"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nazwa użytkownika
                      </label>
                      <input
                        type="text"
                        value={emailSettings.smtp_username}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          smtp_username: e.target.value
                        }))}
                        placeholder="twoj@email.com"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Hasło
                      </label>
                      <input
                        type="password"
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          smtp_password: e.target.value
                        }))}
                        placeholder="Hasło do konta email"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email nadawcy
                      </label>
                      <input
                        type="email"
                        value={emailSettings.from_email}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          from_email: e.target.value
                        }))}
                        placeholder="noreply@firma.pl"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nazwa nadawcy
                      </label>
                      <input
                        type="text"
                        value={emailSettings.from_name}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          from_name: e.target.value
                        }))}
                        placeholder="MACRA Nexus"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        id="use-tls"
                        type="checkbox"
                        checked={emailSettings.use_tls}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          use_tls: e.target.checked
                        }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="use-tls" className="ml-2 block text-sm text-gray-900">
                        Użyj TLS/SSL
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="email-active"
                        type="checkbox"
                        checked={emailSettings.is_active}
                        onChange={(e) => setEmailSettings(prev => ({
                          ...prev,
                          is_active: e.target.checked
                        }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="email-active" className="ml-2 block text-sm text-gray-900">
                        Włącz powiadomienia email
                      </label>
                    </div>
                  </div>
                </div>

                {/* Test Email Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md leading-6 font-medium text-gray-900 mb-4">
                    Testowanie konfiguracji
                  </h4>

                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Wyślij testowy email, aby sprawdzić, czy konfiguracja SMTP działa poprawnie.
                    </p>

                    <div className="flex space-x-4">
                      <button
                        onClick={handleTestEmail}
                        disabled={testingEmail || !emailSettings.is_active}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {testingEmail ? (
                          <>
                            <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Wysyłanie testu...
                          </>
                        ) : (
                          'Wyślij email testowy'
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end border-t border-gray-200 pt-6">
                  <button
                    onClick={handleSaveEmailSettings}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Zapisywanie...
                      </>
                    ) : (
                      'Zapisz ustawienia email'
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Informacje o koncie
                  </h3>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nazwa użytkownika
                      </label>
                      <input
                        type="text"
                        value={user?.username || ''}
                        readOnly
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">Nazwa użytkownika nie może być zmieniona</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        readOnly
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">Email nie może być zmieniony</p>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Imię i nazwisko
                      </label>
                      <input
                        type="text"
                        value={user?.full_name || ''}
                        readOnly
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">Imię i nazwisko nie może być zmienione</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Rola
                      </label>
                      <input
                        type="text"
                        value={user?.role || ''}
                        readOnly
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">Rola nie może być zmieniona</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md leading-6 font-medium text-gray-900 mb-4">
                    Zmiana hasła
                  </h4>
                  <PasswordChangeForm token={token} />
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <UsersManagement token={token} />
            )}

            {activeTab === 'api-keys' && (
              <ApiKeysManagement token={token} />
            )}
          </div>
        </div>
      ) : (
        /* User Settings - Limited Access */
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Ustawienia konta
            </h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nazwa użytkownika
                </label>
                <input
                  type="text"
                  value={user?.username || ''}
                  readOnly
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Nazwa użytkownika nie może być zmieniona</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  readOnly
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Email nie może być zmieniony</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Imię i nazwisko
                </label>
                <input
                  type="text"
                  value={user?.full_name || ''}
                  readOnly
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Imię i nazwisko nie może być zmienione</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rola
                </label>
                <input
                  type="text"
                  value={user?.role || ''}
                  readOnly
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Rola nie może być zmieniona</p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-md leading-6 font-medium text-gray-900 mb-4">
                  Zmiana hasła
                </h4>
                <PasswordChangeForm token={token} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
